import React, { useEffect, useState, useCallback } from "react";
import { api, TIER_LABEL } from "../lib/api";
import { Loader2, Users, DollarSign, Check, X, RotateCcw } from "lucide-react";

const STATUS_COLOR = {
    pending: "bg-amber-100 text-amber-900 border-amber-200",
    approved: "bg-blue-100 text-blue-900 border-blue-200",
    paid: "bg-emerald-100 text-emerald-900 border-emerald-200",
    reversed: "bg-muted text-muted-foreground border-border",
};

export function AdminAffiliatesTab() {
    const [data, setData] = useState({ affiliates: [] });
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        api.get("/admin/affiliates").then(({ data }) => setData(data)).catch(() => setData({ affiliates: [] })).finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggleStatus = async (a) => {
        const next = (a.affiliate_status || "active") === "active" ? "disabled" : "active";
        setSavingId(a.id);
        try { await api.patch(`/admin/affiliates/${a.id}/status`, { status: next }); load(); }
        finally { setSavingId(null); }
    };

    return (
        <div data-testid="admin-affiliates">
            <p className="text-sm text-muted-foreground mb-4">
                Approved referral partners. Disable to stop commissions on new payments (existing pending commissions are not affected).
            </p>
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="affiliates-table">
                    <thead>
                        <tr className="border-b border-border text-left">
                            <th className="ll-label py-2 pr-4">Name / Email</th>
                            <th className="ll-label py-2 pr-4">Code</th>
                            <th className="ll-label py-2 pr-4 text-right">Referred</th>
                            <th className="ll-label py-2 pr-4 text-right">Pending</th>
                            <th className="ll-label py-2 pr-4 text-right">Approved</th>
                            <th className="ll-label py-2 pr-4 text-right">Paid</th>
                            <th className="ll-label py-2 pr-4">Status</th>
                            <th className="ll-label py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.affiliates.map((a) => (
                            <tr key={a.id} className="border-b border-border last:border-0" data-testid={`affiliate-${a.id}`}>
                                <td className="py-2 pr-4">
                                    <div className="font-medium">{a.name}</div>
                                    <div className="text-xs text-muted-foreground">{a.email}</div>
                                </td>
                                <td className="py-2 pr-4 font-mono text-xs">{a.referral_code}</td>
                                <td className="py-2 pr-4 text-right tabular-nums">{a.referred_count}</td>
                                <td className="py-2 pr-4 text-right tabular-nums">${(a.totals?.pending || 0).toFixed(2)}</td>
                                <td className="py-2 pr-4 text-right tabular-nums">${(a.totals?.approved || 0).toFixed(2)}</td>
                                <td className="py-2 pr-4 text-right tabular-nums">${(a.totals?.paid || 0).toFixed(2)}</td>
                                <td className="py-2 pr-4">
                                    <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full border ${(a.affiliate_status || "active") === "active" ? "bg-emerald-100 text-emerald-900 border-emerald-200" : "bg-muted text-muted-foreground border-border"}`}>{a.affiliate_status || "active"}</span>
                                </td>
                                <td className="py-2">
                                    <button onClick={() => toggleStatus(a)} disabled={savingId === a.id}
                                            className="text-xs text-primary hover:underline disabled:opacity-50"
                                            data-testid={`affiliate-toggle-${a.id}`}>
                                        {(a.affiliate_status || "active") === "active" ? "Disable" : "Enable"}
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {data.affiliates.length === 0 && !loading && (
                            <tr><td colSpan={8} className="py-8 text-center text-sm text-muted-foreground">No affiliates yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export function AdminCommissionsTab() {
    const [filter, setFilter] = useState("");
    const [data, setData] = useState({ commissions: [], totals: { pending: 0, approved: 0, paid: 0, reversed: 0 } });
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        const url = `/admin/commissions${filter ? `?status_filter=${filter}` : ""}`;
        api.get(url).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
    }, [filter]);
    useEffect(() => { load(); }, [load]);

    const mark = async (c, kind) => {
        const reason = kind === "reverse" ? prompt("Reason for reversal?", "") : null;
        setSavingId(c.id);
        try {
            if (kind === "paid") await api.post(`/admin/commissions/${c.id}/mark-paid`, { reason: "Manual payout" });
            if (kind === "reverse") await api.post(`/admin/commissions/${c.id}/reverse`, { reason: reason || "admin reversal" });
            load();
        } finally { setSavingId(null); }
    };

    const exportCsv = () => {
        const rows = [["date", "affiliate", "code", "attorney", "tier", "amount", "status", "hold_until", "paid_at"],
            ...data.commissions.map(c => [c.created_at, c.affiliate_name, c.affiliate_referral_code, c.attorney_name, c.tier, c.amount, c.status, c.hold_until, c.paid_at])];
        const csv = rows.map(r => r.map(x => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" }); const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `commissions-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="admin-commissions">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                {["pending", "approved", "paid", "reversed"].map((s) => (
                    <div key={s} className="ll-card">
                        <div className="ll-label">{s}</div>
                        <div className="font-serif text-2xl font-semibold tabular-nums">${(data.totals[s] || 0).toFixed(2)}</div>
                    </div>
                ))}
            </div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="ll-label mr-2">Status:</span>
                {["", "pending", "approved", "paid", "reversed"].map((s) => (
                    <button key={s || "all"} onClick={() => setFilter(s)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${filter === s ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/40"}`}
                            data-testid={`commissions-filter-${s || "all"}`}>
                        {s ? s : "All"}
                    </button>
                ))}
                <button onClick={exportCsv} className="ml-auto text-xs font-semibold rounded-full border border-border px-3 py-1.5 hover:bg-muted/50" data-testid="commissions-export">Export CSV</button>
            </div>
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-border text-left">
                            <th className="ll-label py-2 pr-4">Date</th>
                            <th className="ll-label py-2 pr-4">Affiliate</th>
                            <th className="ll-label py-2 pr-4">Attorney</th>
                            <th className="ll-label py-2 pr-4">Tier</th>
                            <th className="ll-label py-2 pr-4 text-right">Amount</th>
                            <th className="ll-label py-2 pr-4">Status</th>
                            <th className="ll-label py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.commissions.map((c) => (
                            <tr key={c.id} className="border-b border-border last:border-0" data-testid={`commission-${c.id}`}>
                                <td className="py-2 pr-4 text-muted-foreground whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</td>
                                <td className="py-2 pr-4">
                                    <div className="font-medium">{c.affiliate_name}</div>
                                    <div className="text-xs text-muted-foreground font-mono">{c.affiliate_referral_code}</div>
                                </td>
                                <td className="py-2 pr-4">{c.attorney_name}</td>
                                <td className="py-2 pr-4 capitalize">{TIER_LABEL[c.tier] || c.tier}</td>
                                <td className="py-2 pr-4 text-right font-semibold tabular-nums">${c.amount.toFixed(2)}</td>
                                <td className="py-2 pr-4">
                                    <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full border ${STATUS_COLOR[c.status] || ""}`}>{c.status}</span>
                                </td>
                                <td className="py-2">
                                    <div className="flex gap-2 text-xs">
                                        {c.status === "approved" && (
                                            <button onClick={() => mark(c, "paid")} disabled={savingId === c.id}
                                                    className="inline-flex items-center gap-1 text-emerald-700 hover:underline disabled:opacity-50"
                                                    data-testid={`commission-mark-paid-${c.id}`}>
                                                <Check className="h-3 w-3" /> Mark paid
                                            </button>
                                        )}
                                        {["pending", "approved"].includes(c.status) && (
                                            <button onClick={() => mark(c, "reverse")} disabled={savingId === c.id}
                                                    className="inline-flex items-center gap-1 text-destructive hover:underline disabled:opacity-50"
                                                    data-testid={`commission-reverse-${c.id}`}>
                                                <RotateCcw className="h-3 w-3" /> Reverse
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {data.commissions.length === 0 && !loading && (
                            <tr><td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No commissions in this view.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
